import type { OpportunityCustomizationCandidate } from "../../crm_opportunity_workbench/types";

/** 历史定制栏辅助：无关键词时不展示列表。 */
export function filterHistoryCustomizationCandidates(
  items: OpportunityCustomizationCandidate[],
  keyword: string,
): OpportunityCustomizationCandidate[] {
  const normalized = keyword.trim().toLowerCase();
  if (!normalized) {
    return [];
  }
  return items.filter((item) => historyCandidateMatchesKeyword(item, normalized));
}

/** 按定制号、名称、联系人、客户、商机与专业等字段匹配关键词。 */
export function historyCandidateMatchesKeyword(
  item: OpportunityCustomizationCandidate,
  normalizedKeyword: string,
): boolean {
  const haystacks = [
    item.projectCode,
    item.projectName,
    item.contactName,
    item.customerName,
    item.opportunityCode,
    item.opportunityName,
    item.salesUserName,
    item.dispatcherUserName,
    item.travelMonth,
    item.peopleCount != null ? String(item.peopleCount) : null,
    item.durationDays != null ? String(item.durationDays) : null,
    ...item.majorNames,
    ...item.destinationProvinces,
  ];
  return haystacks.some((value) => value?.toLowerCase().includes(normalizedKeyword));
}

/** 历史卡片出行日期：优先精确到日，否则回退月份。 */
export function resolveHistoryCandidateTravelDate(
  item: Pick<OpportunityCustomizationCandidate, "departureStartDate" | "travelMonth">,
): string | undefined {
  const day = item.departureStartDate?.trim();
  if (day) {
    return day;
  }
  return item.travelMonth?.trim() || undefined;
}

/** 历史卡片摘要：销售、计调、联系人与出行要点，去掉重复商机号。 */
export function formatHistoryCustomizationCandidateSummary(
  item: OpportunityCustomizationCandidate,
): string {
  const salesLabel = item.salesUserName?.trim()
    ? `销售${item.salesUserName.trim()}`
    : "销售未配置";
  const dispatcherLabel = item.dispatcherUserName?.trim()
    ? `计调${item.dispatcherUserName.trim()}`
    : "计调未配置";
  const parts = [
    salesLabel,
    dispatcherLabel,
    item.contactName,
    resolveHistoryCandidateTravelDate(item),
    item.peopleCount != null ? `${item.peopleCount}人` : null,
    item.durationDays != null ? `${item.durationDays}天` : null,
  ].filter(Boolean);
  return parts.join(" · ") || "暂无摘要";
}

/** 从高匹配候选提炼快捷搜索词，优先联系人，其次月份。 */
export function suggestHistoryCustomizationKeywords(
  items: OpportunityCustomizationCandidate[],
  limit = 3,
): string[] {
  const suggestions: string[] = [];
  const seen = new Set<string>();
  const push = (value?: string | null) => {
    const normalized = value?.trim();
    if (!normalized) {
      return;
    }
    const key = normalized.toLowerCase();
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    suggestions.push(normalized);
  };

  const ranked = [...items].sort((left, right) => {
    const levelRank = (level: OpportunityCustomizationCandidate["matchLevel"]) =>
      level === "high" ? 0 : level === "review" ? 1 : 2;
    return levelRank(left.matchLevel) - levelRank(right.matchLevel) || right.matchScore - left.matchScore;
  });

  for (const item of ranked) {
    push(item.contactName);
    if (suggestions.length >= limit) {
      return suggestions.slice(0, limit);
    }
  }
  for (const item of ranked) {
    push(item.travelMonth);
    if (suggestions.length >= limit) {
      break;
    }
  }
  return suggestions.slice(0, limit);
}
